import { loadConfig } from "../config";

/**
 * The Cognito Identity Provider API, called straight from the browser (custom
 * auth pages design §4). Public-client operations need no request signing, and
 * the pool client is created with `generateSecret: false`, so there is no
 * SECRET_HASH — the whole transport is one POST with an X-Amz-Target header.
 * `fetch` is injected so tests never touch the network.
 */
export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
}

export interface Tokens {
  idToken: string;
  refreshToken: string;
}

/** Carries the Cognito `__type`; `lib/cognitoErrors` turns that into copy. */
export class CognitoError extends Error {
  constructor(public readonly type: string | undefined) {
    super(type ?? "CognitoError");
    this.name = "CognitoError";
  }
}

type Fetch = (url: string, init: RequestInit) => Promise<Response>;

/** User pool ids are always `<region>_<suffix>`, which saves a config field. */
export function regionOf(userPoolId: string): string {
  return userPoolId.split("_")[0] ?? "";
}

/**
 * The pool signs in by email alias, so the address is the username. Normalizing
 * on the way in means a coach who typed "Coach@Example.com " at signup can sign
 * in with "coach@example.com" later.
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export async function cognitoCall<T>(
  cfg: CognitoConfig,
  operation: string,
  body: Record<string, unknown>,
  fetchImpl: Fetch = fetch,
): Promise<T> {
  const res = await fetchImpl(`https://cognito-idp.${regionOf(cfg.userPoolId)}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify({ ClientId: cfg.clientId, ...body }),
  });

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) {
    // `__type` arrives either bare or namespaced as "com.amazonaws…#TheException".
    const raw = typeof parsed.__type === "string" ? parsed.__type : undefined;
    throw new CognitoError(raw?.split("#").pop());
  }
  return parsed as T;
}

interface AuthResponse {
  AuthenticationResult?: { IdToken?: string; RefreshToken?: string };
  ChallengeName?: string;
}

function tokensFrom(res: AuthResponse, previousRefresh?: string): Tokens {
  const idToken = res.AuthenticationResult?.IdToken;
  // Challenges (MFA, a forced password change) are out of scope: nothing in this
  // app creates one, and treating a challenge as success would sign nobody in.
  if (!idToken) throw new CognitoError(res.ChallengeName ? "ChallengeNotSupported" : undefined);
  return { idToken, refreshToken: res.AuthenticationResult?.RefreshToken ?? previousRefresh ?? "" };
}

export async function signIn(
  cfg: CognitoConfig,
  email: string,
  password: string,
  fetchImpl?: Fetch,
): Promise<Tokens> {
  const res = await cognitoCall<AuthResponse>(
    cfg,
    "InitiateAuth",
    {
      AuthFlow: "USER_PASSWORD_AUTH",
      AuthParameters: { USERNAME: normalizeEmail(email), PASSWORD: password },
    },
    fetchImpl,
  );
  return tokensFrom(res);
}

/** Refresh responses omit RefreshToken — the original stays valid. */
export async function refreshSession(
  cfg: CognitoConfig,
  refreshToken: string,
  fetchImpl?: Fetch,
): Promise<Tokens> {
  const res = await cognitoCall<AuthResponse>(
    cfg,
    "InitiateAuth",
    { AuthFlow: "REFRESH_TOKEN_AUTH", AuthParameters: { REFRESH_TOKEN: refreshToken } },
    fetchImpl,
  );
  return tokensFrom(res, refreshToken);
}

export async function signUp(
  cfg: CognitoConfig,
  email: string,
  password: string,
  fetchImpl?: Fetch,
): Promise<{ UserConfirmed?: boolean }> {
  return cognitoCall<{ UserConfirmed?: boolean }>(
    cfg,
    "SignUp",
    {
      Username: normalizeEmail(email),
      Password: password,
      UserAttributes: [{ Name: "email", Value: normalizeEmail(email) }],
    },
    fetchImpl,
  );
}

export async function confirmSignUp(
  cfg: CognitoConfig,
  email: string,
  code: string,
  fetchImpl?: Fetch,
): Promise<unknown> {
  return cognitoCall(
    cfg,
    "ConfirmSignUp",
    { Username: normalizeEmail(email), ConfirmationCode: code },
    fetchImpl,
  );
}

export async function resendCode(
  cfg: CognitoConfig,
  email: string,
  fetchImpl?: Fetch,
): Promise<unknown> {
  return cognitoCall(cfg, "ResendConfirmationCode", { Username: normalizeEmail(email) }, fetchImpl);
}

export async function forgotPassword(
  cfg: CognitoConfig,
  email: string,
  fetchImpl?: Fetch,
): Promise<unknown> {
  return cognitoCall(cfg, "ForgotPassword", { Username: normalizeEmail(email) }, fetchImpl);
}

export async function confirmForgotPassword(
  cfg: CognitoConfig,
  email: string,
  code: string,
  password: string,
  fetchImpl?: Fetch,
): Promise<unknown> {
  return cognitoCall(
    cfg,
    "ConfirmForgotPassword",
    { Username: normalizeEmail(email), ConfirmationCode: code, Password: password },
    fetchImpl,
  );
}

/** The pages call this rather than threading config through every component. */
export async function cognitoConfig(): Promise<CognitoConfig> {
  const { cognito } = await loadConfig();
  return { userPoolId: cognito.userPoolId, clientId: cognito.clientId };
}
